'use memo'

import { useSelector } from 'src/hooks/useRedux'
import {
  getGenericSportById,
  getSportById,
} from '@sportninja/common/selectors/types'
import { getSportBackgroundImageUrl } from '@sportninja/common/features/sport/sportBackgroundImage'
import type { CompetitionResult } from '@sportninja/common/features/teamFinder/types'

interface SportRecord {
  name?: string | null
  name_full?: string | null
}

interface GenericSportRecord {
  name?: string | null
  name_full?: string | null
  background_web_image?: string | null
}

export interface ResultSport {
  /** Display name for the result's sport (e.g. "Soccer"), or undefined. */
  sportName?: string
  /** Banner art to use when the competition has no image of its own. */
  fallbackBanner: string
}

/**
 * Resolve a Team Finder result's sport to a display label and the banner art to
 * fall back on. A basic-stats (generic) sport is preferred for both, since it's
 * the more specific of the two — e.g. a "Ball Hockey" listing carries the
 * generic sport while `sportId` points at its full-stats parent.
 */
export const useResultSport = (result: CompetitionResult): ResultSport => {
  const sport = useSelector<SportRecord | false>((state) =>
    getSportById(state, result.sportId)
  )
  const genericSport = useSelector<GenericSportRecord | null>((state) =>
    getGenericSportById(state, result.genericSportId)
  )

  const sportName =
    genericSport?.name_full ||
    genericSport?.name ||
    (sport ? sport.name_full || sport.name : undefined) ||
    undefined

  const fallbackBanner = getSportBackgroundImageUrl({
    genericSport,
    isSoccer: (sport ? sport.name : undefined) === 'soccer',
    sport: sport || undefined,
  })

  return { sportName, fallbackBanner }
}
